// Short, per-skill context sentences used to build unique body copy on the
// skill×city landing pages. A per-skill sentence + live per-city data make each
// page read differently with no LLM cost.
export const SKILL_BLURBS = {
  en: {
    construction: "General construction crews handle framing, demolition, concrete, site prep and cleanup",
    landscaping: "Landscaping crews cover mowing, planting, hardscaping, cleanups and seasonal yard work",
    moving: "Movers and loaders handle home and office moves, heavy lifting, loading trucks and hauling",
    painting: "Painters take on interior and exterior repaints, prep, patching and clean finish work",
    plumbing: "Plumbers tackle repairs, installs, drain clearing, fixtures and service calls",
    electrical: "Electricians handle wiring, fixtures, panels, troubleshooting and installations",
    carpentry: "Carpenters build and repair framing, trim, cabinets, decks and custom woodwork",
    cleaning: "Cleaners handle move-outs, post-construction, homes, offices and deep cleans",
    roofing: "Roofers handle repairs, replacements, leak fixes, gutters and inspections",
    drywall: "Drywall crews hang, tape, mud, patch and finish walls and ceilings",
    demolition: "Demolition crews handle teardowns, gutting, debris removal and site clearing",
    flooring: "Flooring pros install and repair tile, hardwood, laminate, vinyl and carpet",
    concrete: "Concrete crews pour and finish driveways, patios, slabs, footings and repairs",
    general_labor: "General laborers handle lifting, hauling, cleanup, digging and helping on any job",
    yard_work: "Yard crews handle mowing, weeding, leaf and brush removal and general upkeep",
    junk_removal: "Junk-removal crews haul away furniture, appliances, debris and clear-outs",
    fence_install: "Fence installers set posts and build wood, vinyl and chain-link fencing and repairs",
    tile_work: "Tile setters install and repair floor, wall, backsplash and bathroom tile",
    mechanical: "Mechanical workers handle equipment repair, HVAC, machinery, pumps, motors and preventive maintenance",
  },
  es: {
    construction: "Las cuadrillas de construcción hacen estructura, demolición, concreto, preparación y limpieza",
    landscaping: "Las cuadrillas de jardinería cortan césped, plantan, hacen paisajismo y limpiezas de temporada",
    moving: "Los cargadores y mudanceros hacen mudanzas de casa y oficina, cargan camiones y acarreos",
    painting: "Los pintores hacen repintado interior y exterior, preparación, resanes y acabados limpios",
    plumbing: "Los plomeros hacen reparaciones, instalaciones, destape de drenajes y llamadas de servicio",
    electrical: "Los electricistas hacen cableado, luminarias, tableros, diagnóstico e instalaciones",
    carpentry: "Los carpinteros construyen y reparan estructura, molduras, gabinetes, terrazas y madera a medida",
    cleaning: "Los de limpieza hacen entregas, post-obra, casas, oficinas y limpiezas profundas",
    roofing: "Los techadores hacen reparaciones, reemplazos, fugas, canaletas e inspecciones",
    drywall: "Las cuadrillas de tablaroca cuelgan, encintan, resanan y dan acabado a muros y techos",
    demolition: "Las cuadrillas de demolición hacen derribos, vaciado, retiro de escombro y limpieza",
    flooring: "Los de pisos instalan y reparan azulejo, madera, laminado, vinil y alfombra",
    concrete: "Las cuadrillas de concreto cuelan y dan acabado a entradas, patios, losas y reparaciones",
    general_labor: "Los ayudantes generales cargan, acarrean, limpian, excavan y apoyan en cualquier trabajo",
    yard_work: "Las cuadrillas de patio cortan césped, deshierban, retiran hojas y dan mantenimiento",
    junk_removal: "Las cuadrillas de retiro llevan muebles, electrodomésticos, escombro y despejes",
    fence_install: "Los instaladores ponen postes y arman cercas de madera, vinil, malla y reparaciones",
    tile_work: "Los azulejeros instalan y reparan azulejo de piso, muro, salpicadero y baño",
    mechanical: "Los mecánicos hacen reparación de equipos, HVAC, maquinaria, bombas, motores y mantenimiento preventivo",
  },
};

export function skillBlurb(lang, skillKey) {
  const map = SKILL_BLURBS[lang] || SKILL_BLURBS.en;
  return map[skillKey] || SKILL_BLURBS.en[skillKey] || "";
}

// Common example jobs per trade — used for a second unique paragraph (common jobs +
// typical pay range) on each skill×city page.
export const SKILL_JOBS = {
  en: {
    construction: "framing, drywall, concrete pours, demolition and general site labor",
    landscaping: "lawn care, tree and hedge trimming, sod, irrigation and yard cleanups",
    moving: "apartment and house moves, furniture assembly, loading and unloading",
    painting: "interior repaints, exterior painting, cabinet refinishing and patching",
    plumbing: "leak repairs, faucet and toilet installs, water heaters and drain cleaning",
    electrical: "outlet and switch installs, lighting, ceiling fans and panel work",
    carpentry: "framing, trim, cabinets, decks, doors and furniture repair",
    cleaning: "move-out cleans, post-construction cleanup, offices and deep cleans",
    roofing: "leak repairs, shingle replacement, gutter work and roof inspections",
    drywall: "hanging, taping, mudding, patching and ceiling repair",
    demolition: "interior gutting, teardowns, debris removal and site clearing",
    flooring: "tile, hardwood, laminate and vinyl install and repair",
    concrete: "driveways, patios, sidewalks, slabs and foundation repairs",
    general_labor: "loading, hauling, digging, site cleanup and crew support",
    yard_work: "mowing, weeding, leaf and brush removal and seasonal cleanups",
    junk_removal: "furniture and appliance hauling, garage and estate clear-outs",
    fence_install: "wood, vinyl and chain-link fence installs and repairs",
    tile_work: "floor, wall, backsplash and bathroom tile install and repair",
    mechanical: "equipment and machine repair, HVAC service, pumps, motors and preventive maintenance",
  },
  es: {
    construction: "estructura, tablaroca, colado de concreto, demolición y labor general",
    landscaping: "cuidado de césped, poda de árboles y setos, riego y limpieza de patios",
    moving: "mudanzas de casa y departamento, armado de muebles, carga y descarga",
    painting: "repintado interior, pintura exterior, gabinetes y resanes",
    plumbing: "reparación de fugas, instalación de llaves y sanitarios, boilers y destapes",
    electrical: "instalación de contactos y apagadores, iluminación, ventiladores y tableros",
    carpentry: "estructura, molduras, gabinetes, terrazas, puertas y reparación de muebles",
    cleaning: "limpiezas de entrega, post-obra, oficinas y limpieza profunda",
    roofing: "reparación de fugas, cambio de teja, canaletas e inspecciones",
    drywall: "colgado, encintado, resane y reparación de techos",
    demolition: "vaciado interior, derribos, retiro de escombro y limpieza de sitio",
    flooring: "instalación y reparación de azulejo, madera, laminado y vinil",
    concrete: "entradas, patios, banquetas, losas y reparación de cimientos",
    general_labor: "carga, acarreo, excavación, limpieza y apoyo a la cuadrilla",
    yard_work: "corte de césped, deshierbe, retiro de hojas y limpiezas de temporada",
    junk_removal: "acarreo de muebles y electrodomésticos, despeje de cocheras",
    fence_install: "instalación y reparación de cercas de madera, vinil y malla",
    tile_work: "instalación y reparación de azulejo de piso, muro y baño",
    mechanical: "reparación de equipos y máquinas, servicio de HVAC, bombas, motores y mantenimiento preventivo",
  },
};

export function skillJobs(lang, skillKey) {
  const map = SKILL_JOBS[lang] || SKILL_JOBS.en;
  return map[skillKey] || SKILL_JOBS.en[skillKey] || "";
}

// A curated, hand-picked set of high-value trade × city combos for the homepage
// "Popular trades near you" internal-link hub.
export const POPULAR_TRADE_LINKS = [
  { skill: "construction", citySlug: "los-angeles-ca", city: "Los Angeles" },
  { skill: "plumbing", citySlug: "houston-tx", city: "Houston" },
  { skill: "landscaping", citySlug: "phoenix-az", city: "Phoenix" },
  { skill: "moving", citySlug: "new-york-ny", city: "New York" },
  { skill: "painting", citySlug: "chicago-il", city: "Chicago" },
  { skill: "electrical", citySlug: "dallas-tx", city: "Dallas" },
  { skill: "cleaning", citySlug: "miami-fl", city: "Miami" },
  { skill: "carpentry", citySlug: "san-antonio-tx", city: "San Antonio" },
  { skill: "roofing", citySlug: "atlanta-ga", city: "Atlanta" },
  { skill: "concrete", citySlug: "san-diego-ca", city: "San Diego" },
  { skill: "general_labor", citySlug: "san-francisco-ca", city: "San Francisco" },
  { skill: "landscaping", citySlug: "austin-tx", city: "Austin" },
];
